import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type {
  AdminBookingsResponse,
  AdminBookingDetailResponse,
  AdminCreateBookingRequest,
} from '@/api/types';

export type AdminBookingFilters = {
  startDate?: string;
  endDate?: string;
  locationId?: string;
  unitId?: string;
  sportCode?: string;
  status?: string;
  search?: string;
  page?: number;
  size?: number;
};

function buildParams(filters: AdminBookingFilters): string {
  const params = new URLSearchParams();
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (filters.locationId) params.set('locationId', filters.locationId);
  if (filters.unitId) params.set('unitId', filters.unitId);
  if (filters.sportCode) params.set('sportCode', filters.sportCode);
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('search', filters.search);
  params.set('page', String(filters.page ?? 0));
  params.set('size', String(filters.size ?? 20));
  return params.toString();
}

export function useAdminBookings(filters: AdminBookingFilters) {
  return useQuery({
    queryKey: ['adminBookings', filters],
    queryFn: () =>
      apiClient<AdminBookingsResponse>(`/admin/bookings?${buildParams(filters)}`),
  });
}

export function useAdminBookingDetail(id?: string) {
  return useQuery({
    queryKey: ['adminBookingDetail', id],
    queryFn: () =>
      apiClient<AdminBookingDetailResponse>(`/admin/bookings/${id}`),
    enabled: !!id,
  });
}

export function useCreateWalkIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: AdminCreateBookingRequest) =>
      apiClient<AdminBookingDetailResponse>('/admin/bookings', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminBookings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardSummary'] });
    },
  });
}
